import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, ClipboardCheck, ShieldCheck, AlertTriangle, Hourglass, Inbox, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const fmt = (v, dash = "—") => (v === null || v === undefined ? dash : v);
const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const KPI = ({ icon: Icon, label, value, tone = "slate", testId }) => {
  const tones = {
    slate:   "bg-white border-slate-200 text-slate-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  }[tone];
  const iconTone = {
    slate: "text-slate-500", cyan: "text-cyan-600", amber: "text-amber-600",
    emerald: "text-emerald-600", rose: "text-rose-600",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-2xl border p-4 ${tones}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold opacity-70">
        <Icon className={`w-3.5 h-3.5 ${iconTone}`} /> {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1.5">{value}</div>
    </div>
  );
};

export function LabThroughputWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/lab/throughput")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200 p-7 mb-7">
        <div className="text-sm text-slate-500">Loading lab throughput…</div>
      </div>
    );
  }
  if (!data) return null;

  const max = Math.max(1, ...data.trend_7d.flatMap((d) => [d.received, d.published]));

  return (
    <section data-testid="lab-throughput-widget" className="rounded-3xl bg-white border border-slate-200 p-7 mb-7">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-600 inline-flex items-center gap-2">
            <FlaskConical className="w-3.5 h-3.5" /> Lab throughput
          </div>
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-1">Today &amp; the last 7 days</h2>
        </div>
        <Link
          to="/admin/lab"
          data-testid="lab-throughput-open"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors"
        >
          Open lab <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="lab-kpi-received-today"  icon={Inbox}          label="Received today"   value={fmt(data.received_today)}   tone="cyan" />
        <KPI testId="lab-kpi-entered-today"   icon={ClipboardCheck} label="Results today"    value={fmt(data.entered_today)}    tone="slate" />
        <KPI testId="lab-kpi-published-today" icon={ShieldCheck}    label="Published today"  value={fmt(data.published_today)}  tone="emerald" />
        <KPI testId="lab-kpi-turnaround"      icon={Clock}          label="Median turnaround" value={fmtHours(data.median_turnaround_hours)} tone="slate" />
        <KPI testId="lab-kpi-awaiting"        icon={Hourglass}      label="Awaiting review"  value={fmt(data.awaiting_review)}  tone={data.awaiting_review > 0 ? "amber" : "slate"} />
        <KPI testId="lab-kpi-pending"         icon={FlaskConical}   label="Samples pending"  value={fmt(data.pending_now)}      tone="slate" />
        <KPI testId="lab-kpi-overdue"         icon={AlertTriangle}  label="Overdue (>5d)"    value={fmt(data.overdue_pending)}  tone={data.overdue_pending > 0 ? "rose" : "slate"} />
        <KPI testId="lab-kpi-asof"            icon={Clock}          label="As of"            value={new Date(data.as_of).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })} tone="slate" />
      </div>

      {/* Turnaround by test type */}
      {data.turnaround_by_test && (
        <div data-testid="lab-turnaround-by-test" className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Sample turnaround (last 30 days)
            </div>
            <div className="text-xs font-semibold text-slate-700">
              Overall median: <span className="font-display font-bold text-slate-900">{fmtHours(data.median_turnaround_hours)}</span>
            </div>
          </div>
          {(() => {
            const TEST_LABELS = {
              basic: "AWARE 4-Point",
              tank:  "AWARE Tank 360",
              bore:  "AWARE Bore 360",
            };
            const entries = ["basic", "tank", "bore"].map((tid) => ({
              tid, label: TEST_LABELS[tid],
              row: data.turnaround_by_test[tid],
            })).filter((e) => e.row && e.row.sample_count > 0);
            if (entries.length === 0) {
              return <div className="text-xs text-slate-400 italic">Not enough completed samples yet</div>;
            }
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {entries.map((e) => (
                  <div key={e.tid} data-testid={`lab-turnaround-${e.tid}`} className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 truncate">{e.label}</div>
                      <div className="text-[10px] text-slate-400">{e.row.sample_count} sample{e.row.sample_count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="font-display text-lg font-bold text-slate-900 whitespace-nowrap">{fmtHours(e.row.median_hours)}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* 7-day mini-trend */}
      <div data-testid="lab-throughput-trend" className="mt-6">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-3 flex items-center gap-4">
          <span>Last 7 days</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-500" /> Received</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Published</span>
        </div>
        <div className="flex items-end gap-2 h-20">
          {data.trend_7d.map((d) => {
            const rh = (d.received / max) * 100;
            const ph = (d.published / max) * 100;
            const label = new Date(d.date).toLocaleDateString("en-AU", { weekday: "short" });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full flex items-end gap-0.5 h-full">
                  <div className="flex-1 bg-cyan-500/80 rounded-t" style={{ height: `${rh}%`, minHeight: d.received > 0 ? 2 : 0 }} title={`${d.received} received`} />
                  <div className="flex-1 bg-emerald-500/80 rounded-t" style={{ height: `${ph}%`, minHeight: d.published > 0 ? 2 : 0 }} title={`${d.published} published`} />
                </div>
                <div className="text-[10px] text-slate-400 font-medium">{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
